"use client";

import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import {
  APP_STORE_URLS,
  trackAppStoreClick,
} from "@/lib/app-download";

interface AppDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AppDownloadModal({ isOpen, onClose }: AppDownloadModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[10050] flex items-center justify-center p-4"
          onClick={onClose}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" } as React.CSSProperties}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full pt-10 pb-5 px-5 sm:pt-12 sm:pb-6 sm:px-6 relative z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Logo - Half outside, half inside */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-8 sm:-top-10">
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center p-2 sm:p-3 shadow-lg border-4 border-white"
                style={{ backgroundColor: "#2BA6E6" }}
              >
                <Image
                  src="/assets/brand/realtorsin-logo-light.png"
                  alt="RealtorsIn Logo"
                  width={56}
                  height={56}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Header */}
            <div className="text-center mb-2">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                Download Now
              </h2>
            </div>

            {/* Product Description */}
            <div className="mb-5 sm:mb-6 px-2">
              <p className="text-xs sm:text-sm text-gray-700 text-center leading-relaxed">
                Join thousands of real estate professionals. Connect, network, and grow your
                business with the #1 professional platform for realtors.
              </p>
              <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>10K+ Members</span>
                </div>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Free to Join</span>
                </div>
              </div>
            </div>

            {/* App Store Buttons */}
            <div className="space-y-3">
              <a
                href={APP_STORE_URLS.android}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackAppStoreClick("android");
                  onClose();
                }}
                className="block group w-full text-left"
              >
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center bg-white border-2 border-gray-200 px-4 sm:px-5 py-5 rounded-xl shadow-sm transition-all duration-300 group-hover:shadow-lg group-hover:border-gray-300"
                >
                  <Image
                    src="/assets/brand/google-play-store-badge.png"
                    alt="Google Play"
                    width={40}
                    height={40}
                    className="flex-shrink-0 w-10 h-10"
                  />
                  <div className="ml-3 sm:ml-4 text-left flex-1 min-w-0">
                    <div className="text-xs sm:text-sm text-gray-700 leading-tight">
                      Get it on
                    </div>
                    <div className="text-base sm:text-lg font-bold text-gray-900 leading-tight -mt-0.5">
                      Google Play
                    </div>
                  </div>
                </motion.div>
              </a>

              <a
                href={APP_STORE_URLS.ios}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackAppStoreClick("ios");
                  onClose();
                }}
                className="block group w-full text-left"
              >
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center bg-white border-2 border-gray-200 pl-1.5 pr-4 sm:pr-5 py-3 rounded-xl shadow-sm transition-all duration-300 group-hover:shadow-lg group-hover:border-gray-300"
                >
                  <svg
                    className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0"
                    viewBox="0 0 48 48"
                    fill="none"
                  >
                    <path
                      d="M37.4178 25.9048C37.3878 21.7048 40.8778 19.6648 41.0478 19.5548C39.0978 16.6048 35.9578 16.1948 34.8178 16.1548C32.1478 15.8648 29.5578 17.7548 28.1978 17.7548C26.8078 17.7548 24.7178 16.1848 22.4578 16.2348C19.5078 16.2848 16.7478 17.9848 15.2378 20.6548C12.1178 26.1048 14.4178 34.2548 17.4178 38.7548C18.9178 40.9548 20.6778 43.4248 23.0378 43.3448C25.2978 43.2548 26.1478 41.9548 28.8078 41.9548C31.4378 41.9548 32.2178 43.3448 34.5378 43.2848C36.9278 43.2548 38.4378 41.0648 39.8778 38.8448C41.5978 36.3348 42.3078 33.8848 42.3378 33.7448C42.2778 33.7248 37.4478 31.9148 37.4178 25.9048Z"
                      fill="#000000"
                    />
                    <path
                      d="M32.4278 12.7448C33.6478 11.2448 34.4878 9.19477 34.2678 7.11477C32.5278 7.19477 30.3678 8.32477 29.0878 9.79477C27.9578 11.0948 26.9378 13.2148 27.1878 15.2148C29.1478 15.3548 31.1778 14.2248 32.4278 12.7448Z"
                      fill="#000000"
                    />
                  </svg>
                  <div className="ml-3 sm:ml-4 text-left flex-1 min-w-0">
                    <div className="text-xs sm:text-sm text-gray-700 leading-tight">
                      Download on the
                    </div>
                    <div className="text-base sm:text-lg font-bold text-gray-900 leading-tight -mt-0.5">
                      Apple Store
                    </div>
                  </div>
                </motion.div>
              </a>
            </div>

            {/* Footer Text */}
            <div className="mt-4 sm:mt-5 text-center">
              <p className="text-xs text-gray-500">
                Available for Android 6.0+ • iOS 15.6+
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
